import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/**
 * Builds a section selector ID
 * @param {string} sectionId - The section ID
 * @returns {string} The section selector
 */
function buildSectionSelector(sectionId) {
  return `shopify-section-${sectionId}`;
}

/**

 * @param {Node} node - The node to check
 * @returns {string|undefined} The cart item key
 */
function getCartItemKeyFromParent(node) {
  if (!(node instanceof Element)) return undefined;
  const parentRow = node.closest('[data-key]');
  return parentRow?.getAttribute('data-key') ?? undefined;
}

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * If the removed item is a main product, also removes linked add-ons
   * (`_upsell` + `_linkedProduct` or `_tieredPdpBundle` + `_linkedProduct`).
   * @param {number} line - The line item index (1-based).
   */
  onLineItemRemove(line) {
    const cartItemRow = this.refs.cartItemRows[line - 1];
    const variantId = cartItemRow?.getAttribute('data-variant-id');
    const rowKey = cartItemRow?.getAttribute('data-key');

    const keysToRemove = rowKey ? [rowKey] : [];

    if (variantId) {
      this.refs.cartItemRows.forEach((row) => {
        if (row.getAttribute('data-upsell') === 'true' && row.getAttribute('data-linked-product') === String(variantId)) {
          const key = row.getAttribute('data-key');
          if (key && !keysToRemove.includes(key)) keysToRemove.push(key);
        }
      });
    }

    if (keysToRemove.length <= 1) {
      this.updateQuantity({
        line,
        quantity: 0,
        action: 'clear',
      });
      return;
    }

    this.#removeItemsByKeys(keysToRemove);
  }

  /**
   * Removes multiple cart items by line item keys (e.g. main + linked upsells) via cart/update.js.
   * @param {string[]} keys - Line item keys to set quantity to 0.
   */
  #removeItemsByKeys(keys) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker('clear:user-action');
    this.#disableCartItems();

    const keySet = new Set(keys);
    this.refs.cartItemRows.forEach((row) => {
      if (keySet.has(row.getAttribute('data-key'))) row.classList.add('updating');
    });

    const updates = {};
    keys.forEach((key) => {
      updates[key] = 0;
    });

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const updateUrl =
      (Theme.routes.cart_update_url || '').includes('.js')
        ? Theme.routes.cart_update_url
        : Theme.routes.cart_update_url + '.js';

    fetch(updateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
    })
      .then((response) => response.json())
      .then((response) => {
        resetShimmer(this);
        if (response.status && response.message) {
          throw new Error(response.message);
        }
        const newItemCount = response.item_count ?? 0;
        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newItemCount,
            source: 'cart-items-component',
            sections: response.sections,
          })
        );
        return sectionRenderer.renderSection(this.sectionId, { cache: false });
      })
      .then(() => {
        document.dispatchEvent(new CustomEvent('section:rendered', { detail: { sectionId: this.sectionId } }));
      })
      .catch((error) => {
        console.error('Cart update failed:', error);
      })
      .finally(() => {
        this.#enableCartItems();
        this.#clearUpdatingStates();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    this.#markLineUpdating(line);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        this.#clearUpdatingStates();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      const fragment = new DOMParser().parseFromString(cartItemsHtml, 'text/html');
      const existingElement = document.getElementById(buildSectionSelector(this.sectionId));
      const newElement = fragment.getElementById(buildSectionSelector(this.sectionId));
      
      if (existingElement && newElement) {
        morph(existingElement, newElement, {
          childrenOnly: true,
          getNodeKey: (node) => {
            if (node instanceof Element && node.classList.contains('cart-items__media-container')) {
              return getCartItemKeyFromParent(node);
            }
            if (node instanceof Element && node.hasAttribute('data-key')) {
              return node.getAttribute('data-key') ?? undefined;
            }
            return node instanceof Element ? node.id : undefined;
          },
        });
      } else {
        morphSection(this.sectionId, cartItemsHtml);
      }
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  #markLineUpdating(/** @type {number} */ line) {
    const cartItemRow = this.refs.cartItemRows[line - 1];

    if (!cartItemRow) return;

    const rowsToMark = [
      cartItemRow,
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRow.dataset.key),
    ];

    rowsToMark.forEach((row) => row.classList.add('updating'));
  }

  #clearUpdatingStates() {
    this.querySelectorAll('.cart-items__table-row.updating').forEach((row) => {
      row.classList.remove('updating');
    });
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
